"use client"

import { Slot } from "@radix-ui/react-slot"
import { cva, VariantProps } from "class-variance-authority"
import React, { forwardRef, PropsWithChildren, ReactElement, ReactNode } from "react"
import PendingCircle from "src/components/icon/PendingCircle"
import { cn } from "src/lib/utils"
import { Typography, UI_VARIANTS } from "src/orca/components/Typography/typography"

export type ButtonVariants = VariantProps<typeof buttonVariants>

export const buttonVariants = cva(
  "flex justify-center focus-visible:shadow-tokens-shadow-02 whitespace-nowrap items-center rounded-tokens-button relative",
  {
    variants: {
      variant: {
        primary:
          "bg-tokens-surface-button-primary-default text-tokens-text-onbutton-primary-default hover:text-tokens-text-onbutton-primary-hover-active hover:bg-tokens-surface-button-primary-hover-active focus-visible:bg-tokens-surface-button-primary-hover-active focus-visible:text-tokens-text-onbutton-primary-hover-active current:bg-tokens-surface-button-primary-hover-active active:bg-tokens-surface-button-primary-pressed current:text-tokens-text-onbutton-primary-hover-active active:text-tokens-text-onbutton-primary-pressed",
        secondary:
          "bg-tokens-surface-button-secondary-default text-tokens-text-onbutton-secondary-default hover:text-tokens-text-onbutton-secondary-hover-active hover:bg-tokens-surface-button-secondary-hover-active focus-visible:bg-tokens-surface-button-secondary-hover-active focus-visible:text-tokens-text-onbutton-secondary-hover-active current:bg-tokens-surface-button-secondary-hover-active current:text-tokens-text-onbutton-secondary-hover-active active:bg-tokens-surface-button-secondary-pressed active:text-tokens-text-onbutton-secondary-pressed",
        ghost:
          "text-tokens-text-onbutton-ghost-default hover:text-tokens-text-onbutton-ghost-hover-active hover:bg-tokens-surface-button-ghost-hover-active focus-visible:bg-tokens-surface-button-ghost-hover-active focus-visible:text-tokens-text-onbutton-ghost-hover-active current:bg-tokens-surface-button-ghost-hover-active current:text-tokens-text-onbutton-ghost-hover-active active:bg-tokens-surface-button-ghost-pressed active:text-tokens-text-onbutton-ghost-pressed",
        error:
          "bg-tokens-surface-button-error-default text-tokens-text-onbutton-error-default hover:text-tokens-text-onbutton-error-hover-active hover:bg-tokens-surface-button-error-hover-active focus-visible:bg-tokens-surface-button-error-hover-active focus-visible:text-tokens-text-onbutton-error-hover-active current:bg-tokens-surface-button-error-hover-active current:text-tokens-text-onbutton-error-hover-active active:bg-tokens-surface-button-error-pressed active:text-tokens-text-onbutton-error-pressed",
        positive:
          "bg-tokens-surface-button-positive-default text-tokens-text-onbutton-positive-default hover:text-tokens-text-onbutton-positive-hover-active hover:bg-tokens-surface-button-positive-hover-active focus-visible:bg-tokens-surface-button-positive-hover-active focus-visible:text-tokens-text-onbutton-positive-hover-active current:bg-tokens-surface-button-positive-hover-active current:text-tokens-text-onbutton-positive-hover-active active:bg-tokens-surface-button-positive-pressed active:text-tokens-text-onbutton-positive-pressed",
        informative:
          "bg-tokens-surface-button-informative-default text-tokens-text-onbutton-informative-default hover:text-tokens-text-onbutton-informative-hover-active hover:bg-tokens-surface-button-informative-hover-active focus-visible:bg-tokens-surface-button-informative-hover-active focus-visible:text-tokens-text-onbutton-informative-hover-active current:bg-tokens-surface-button-informative-hover-active current:text-tokens-text-onbutton-informative-hover-active active:bg-tokens-surface-button-informative-pressed active:text-tokens-text-onbutton-informative-pressed",
      },
      size: {
        48: "px-tokens-space-24 h-tokens-height-48",
        40: "px-tokens-space-16 h-tokens-height-40",
        32: "px-tokens-space-12 h-tokens-height-32",
      },
      disabled: {
        true: "!bg-tokens-surface-button-disabled !hover:bg-tokens-surface-button-disabled !current:bg-tokens-surface-button-disabled !text-tokens-text-onbutton-disabled !hover:text-tokens-text-onbutton-disabled !current:text-tokens-text-onbutton-disabled cursor-not-allowed",
      },
    },
    compoundVariants: [
      // ghost button should retain its default bg color
      {
        variant: "ghost",
        disabled: true,
        className: "!bg-transparent",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: 40,
    },
  },
)

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariants {
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  loading?: boolean
  testId?: string
  wrapper?: ReactElement
  disabled?: boolean
}

// TODO
// [ ] Button size 32 = loader 16x16; Button size 40, 48 = loader 20x20; (c) Casper
// [ ] Button size 32 = icon 16x16; Button size 40 = icon 16x16 OR 20x20; Button size 40 = icon 20x20; (c) Casper
// [ ] Use getByRole instead of getByTestId in tests
export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      type = "button",
      className,
      variant,
      size,
      wrapper,
      children,
      rightIcon,
      leftIcon,
      loading = false,
      disabled,
      testId,
      ...props
    },
    ref,
  ) => {
    const typographyVariant = {
      32: "ui13",
      40: "ui14",
      48: "ui15",
    }[size || 40] as (typeof UI_VARIANTS)[number]

    const Comp = wrapper ? Slot : "button"
    const content = (
      <>
        {loading && (
          <div
            className="absolute flex items-center justify-center"
            data-testid={testId && `${testId}-loading`}
          >
            <PendingCircle className="size-[20px]" />
          </div>
        )}
        <div
          className={cn(
            loading && "invisible",
            "flex items-center",
            size === 32 ? "gap-tokens-space-6" : "gap-tokens-space-8",
          )}
          data-testid={testId && `${testId}-children`}
        >
          {leftIcon}
          <Typography variant={typographyVariant} weight="medium" color="inherit" level="span">
            {children}
          </Typography>
          {rightIcon}
        </div>
      </>
    )

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, disabled }), className)}
        disabled={disabled || loading}
        ref={ref}
        data-testid={testId}
        type={!wrapper ? type : undefined}
        data-auto-tracking="true"
        {...props}
      >
        {wrapper ? React.cloneElement(wrapper, {}, content) : content}
      </Comp>
    )
  },
)

Button.displayName = "Button"
